<template>
  <ak-loading
    v-model:active="wizardLoading"
    :is-full-page="fullPageLoading"
    :can-cancel="false"
    spinner-size="large"
  />

  <ak-sidebar-layout
    class="create-app bg-grey-lightest"
    size="huge"
    sidebar-style="light"
  >
    <template #sidebar>
      <ak-box>
        <ak-form ref="sidebarForm">
          <h4 class="color-grey">Create Form</h4>
          <ak-field label="Form Name" required>
            <ak-popover ref="popper" open-once placement="right" class="block">
              <template #trigger>
                <ak-input
                  ref="titleInput"
                  v-model="app.title"
                  required
                  :custom-rules="customValidationRules"
                />
              </template>
              <template #popover>
                <ak-pack>
                  <ak-pack-item>
                    <ak-icon size="huge" type="bulb" color="quaternary" />
                  </ak-pack-item>
                  <ak-pack-item>
                    Start by naming your form and assigning it to a project.
                  </ak-pack-item>
                </ak-pack>
              </template>
            </ak-popover>
          </ak-field>

          <template v-if="!wizardLoading">
            <ak-project-form-select
              v-model:id="app.campaign"
              v-model:label="app.campaignLabel"
            />
          </template>

          <a
            v-if="!showAdvancedOptions"
            class="text-small cursor-pointer"
            @click="toggleAdvanced"
            ><strong>[+] Show Advanced</strong></a
          >
          <a
            v-if="showAdvancedOptions"
            class="text-small cursor-pointer"
            @click="toggleAdvanced"
            ><strong>[-] Hide Advanced</strong></a
          >

          <template v-if="showAdvancedOptions">
            <div class="flex mt-small">
              <ak-field label="Start Date" class="one-half">
                <template #helper>Optional start date</template>
                <ak-date-picker
                  v-model="app.startDate"
                  :custom-rules="startDateValidation"
                  :config="pickerConfig"
                />
              </ak-field>
              <ak-field label="End Date" class="one-half ml-small">
                <template #helper>Optional end date</template>
                <ak-date-picker
                  v-model="app.endDate"
                  :custom-rules="endDateValidation"
                  :config="pickerConfig"
                />
              </ak-field>
            </div>
          </template>
        </ak-form>
      </ak-box>
    </template>

    <template #body>
      <ak-box class="relative">
        <div
          v-if="!app.title || !app.campaign"
          v-show="!wizardLoading"
          class="overlay z-index--20"
        ></div>

        <ak-cards>
          <template v-for="layout in layouts" :key="layout._id">
            <ak-cards-item
              class="h-275 one-whole lap--one-whole small-screen-up--one-half desk--one-half desk-xl--one-third"
            >
              <template v-if="layout.name !== 'Blank'">
                <ak-card @click="createNewApp($event, layout)">
                  <ak-overlay
                    class="h-full cursor-pointer"
                    is-primary
                    has-radius
                  >
                    <ak-card-body class="h-full flex--column">
                      <ak-loading
                        v-model:active="layout.creating"
                        :is-full-page="false"
                        :can-cancel="false"
                        spinner-size="default"
                      />
                      <h4 class="flex color-action mb-small">
                        {{ layout.name }}
                        <ak-icon
                          :type="layout.icon"
                          size="large"
                          color="grey-light"
                          class="ml-auto"
                        />
                      </h4>
                      <p>{{ layout.description }}</p>
                    </ak-card-body>
                    <template #overlayBody>
                      <div class="flex flex--column flex--center">
                        <ak-icon
                          :type="layout.icon"
                          size="large"
                          color="white"
                        />
                        <strong class="mt-tiny"
                          >Start with {{ layout.name }}</strong
                        >
                      </div>
                    </template>
                  </ak-overlay>
                </ak-card>
              </template>

              <template v-else>
                <ak-card
                  class="empty-state empty-state--new"
                  @click="createNewApp($event, layout)"
                >
                  <ak-loading
                    v-model:active="layout.creating"
                    :is-full-page="false"
                    :can-cancel="false"
                    spinner-size="huge"
                  />
                  <ak-empty-state icon="editor-form" title="Create your own" />
                </ak-card>
              </template>
            </ak-cards-item>
          </template>
        </ak-cards>
      </ak-box>
    </template>
  </ak-sidebar-layout>
</template>

<script>
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Popover from '@akerolabs/akero-vue/src/components/popover/Popover.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import ProjectFormSelect from '../../projects/ProjectFormSelect.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import Cards from '@akerolabs/akero-vue/src/components/cards/Cards.vue'
import CardsItem from '@akerolabs/akero-vue/src/components/cards/CardsItem.vue'
import Card from '@akerolabs/akero-vue/src/components/cards/Card.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import EmptyState from '@akerolabs/akero-vue/src/components/emptyState/EmptyState.vue'
import Overlay from '@akerolabs/akero-vue/src/components/overlay/Overlay.vue'
import date from '@akerolabs/akero-vue/src/utils/date'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { merge, isObject } from '@akerolabs/akero-vue/src/utils/helpers'
import { defineComponent, ref, onMounted } from 'vue'
import { accountObj, agencyObj, userObj } from '@akerolabs/akero-vue/src/store'
import { ignoreTimeZoneISO } from '@akerolabs/akero-vue/src/services/localisation'
import { sendEvent } from '../../../services/intercom.io'
import { getStartEndValidation } from '../../../utils/validationRules'
import { LocalisationFactory } from '@akerolabs/akero-vue/src/services/localisation'

import {
  appSpecificOptionsFactory,
  appAutoresponseTemplatesFactory,
} from '../../../services/apps'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'

import { uniqueValidtion } from '../../../utils/validationRules'

import orderBy from 'lodash/orderBy'

export default defineComponent({
  name: 'AkFormEmbedWizard',

  components: {
    [SidebarLayout.name]: SidebarLayout,
    [Box.name]: Box,
    [Form.name]: Form,
    [Field.name]: Field,
    [Input.name]: Input,
    [Popover.name]: Popover,
    [Icon.name]: Icon,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Loading.name]: Loading,
    [Cards.name]: Cards,
    [CardsItem.name]: CardsItem,
    [Card.name]: Card,
    [CardBody.name]: CardBody,
    [EmptyState.name]: EmptyState,
    [Overlay.name]: Overlay,
    [ProjectFormSelect.name]: ProjectFormSelect,
  },

  props: {
    onCancel: {
      type: Function,
      default: () => ({}),
    },
    onFinished: {
      type: Function,
      default: () => ({}),
    },
    wizardType: {
      type: String,
      default: undefined,
    },
    activeCampaign: {
      type: String,
      default: undefined,
    },
    cancelText: {
      type: String,
      default: 'Cancel',
    },
    fullPageLoading: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    const appSpecificOptions = appSpecificOptionsFactory()
    const appAutoresponseTemplates = appAutoresponseTemplatesFactory()
    const localisation = LocalisationFactory()

    const formVersion = 2
    const upperLimit = date().startOf('Day').add(10, 'years').toISOString()
    const lowerLimit = date().startOf('Day').subtract(10, 'years').toISOString()
    // const minLimitStartDate = date().toISOString()
    // const maxLimitEndDate = date().add(3, 'months').toISOString()
    // const minLimitEndDate = minLimitStartDate

    const { _id: accountId, name: accountName, slug } = accountObj()
    const { _id: agencyId } = agencyObj()
    const { _id: userId } = userObj()

    const wizardLoading = ref(true)
    const disableCampaignSelect = ref(false)
    const sidebarForm = ref(null)
    const popper = ref(null)
    const showAdvancedOptions = ref(false)
    const layoutsLoading = ref(true)
    const layouts = ref([])
    const campaigns = ref([])
    const account = ref({})
    const minStartDate = ref(lowerLimit)
    const maxStartDate = ref(upperLimit)
    const minEndDate = ref(lowerLimit)
    const maxEndDate = ref(upperLimit)
    let appCreating = false

    const customValidationRules = ref([
      uniqueValidtion({
        message: 'Name has already been used',
        type: 'embed',
        account: () => accountObj()._id,
        agency: () => agencyObj()._id,
        property: 'slug',
        slugify: true,
      }),
    ])

    const app = ref({
      title: '',
      campaign: '',
      campaignLabel: '',
    })

    const toggleAdvanced = () => {
      showAdvancedOptions.value = !showAdvancedOptions.value
    }

    const fetchLayouts = async () => {
      const { data: res } = await request.get('/form-templates', {
        props: {
          hidden: JSON.stringify({ $ne: true }),
          deleted: JSON.stringify({ $ne: true }),
          per_page: 0,
        },
      })

      layouts.value = orderBy(
        res.data,
        ['isNewLayout', 'order'],
        ['desc', 'desc'],
      )
    }

    const fetchAccount = async () => {
      const { data } = await request.get(`/accounts/${accountObj()._id}`, {
        params: {
          agency: agencyObj()._id,
          overrideAccess: true,
        },
      })
      account.value = data.data
    }

    const fetchCampaigns = async () => {
      const { data: res } = await request.get('/campaigns', {
        params: {
          account: accountId,
          agency: agencyId,
          deleted: JSON.stringify({ $ne: true }),
          archived: JSON.stringify({ $ne: true }),
          per_page: 0,
        },
      })

      campaigns.value = res.data
    }

    const fetchSchema = async () => {
      const { data } = await request.get('/schema/app')
      app.value = merge(app.value, data.data, true)
      app.value.appVersion = 2
      // Force the form border to be not so in your face
      app.value.draft.styles.accent1 = '#ccc'
      delete app.value._id
      delete app.value.id
      delete app.value.description
    }

    const setActiveCampaign = () => {
      if (props.activeCampaign) {
        const id = isObject(props.activeCampaign)
          ? props.activeCampaign._id
          : props.activeCampaign

        const active = campaigns.value.find((campaign) => {
          return campaign._id === id
        })

        if (active) {
          app.value.campaign = active._id
          app.value.campaignLabel = active.title
          disableCampaignSelect.value = false
        }
      }
    }

    const setAccountTags = async () => {
      if (app.value.trackingConfig.providers === undefined) {
        app.value.trackingConfig.providers = []
        if (account.value.tags && account.value.tags.googleTagManager) {
          app.value.trackingConfig.providers.push({
            type: 'googletagmanager',
            values: [account.value.tags.googleTagManager],
          })
        }
        if (account.value.tags && account.value.tags.googleAnalytics) {
          app.value.trackingConfig.providers.push({
            type: 'google',
            values: [account.value.tags.googleAnalytics],
          })
        }
      }
    }

    const getAppSpecificOptions = (form) => {
      return new Promise((resolve) => {
        appSpecificOptions.get('embed', (options) => {
          merge(form.appSpecificOptions, options)
          resolve(form)
        })
      })
    }

    const getAutoResponseTemplate = async () => {
      appAutoresponseTemplates.get('embed', (template) => {
        app.value.leadsConfig.autoresponseTemplate = template.email
        app.value.leadsConfig.autoresponseSMSTemplate = template.sms
        app.value.fbConfig.fbShareName = app.value.title
        app.value.fbConfig.fbShareDescription = 'Share this with your friends'
      })
    }

    const saveNewApp = async (app) => {
      const { data: res } = await request.post('/apps', app)
      const { data, status } = res
      const url = `${appsUrl}/${slug}/${data.slug}`

      if (status === 'success') {
        sendEvent({
          eventName: 'completed-form-embed-wizard',
          metaData: {
            app_id: data._id,
            app_name: data.title,
            app_url: url,
            app_type: 'form-embed',
          },
        })

        await onFinished(res, data)
      } else {
        ToastProgrammatic.open({
          duration: 5000,
          type: 'danger',
          message: 'Failed to create form',
        })

        await onCancel()
      }
    }

    const createNewApp = async (e, layout) => {
      if (appCreating) return

      appCreating = true
      layout.creating = true

      const isInvalid = await validateForm()

      if (isInvalid) {
        layout.creating = false
        appCreating = false
        return
      }

      layout.form.postEnabled = false
      layout.form.postContent = '<p>View our [[PRIVACY_POLICY_LINK]]</p>'

      const newApp = merge({}, app.value, true)

      newApp.draft.formLayout = layout.form
      newApp.live.formLayout = layout.form
      newApp.startDate = ignoreTimeZoneISO(newApp.startDate)
      newApp.endDate = ignoreTimeZoneISO(newApp.endDate)

      await getAutoResponseTemplate()
      await saveNewApp(newApp)
    }

    const validateForm = async () => {
      const isValid = await sidebarForm.value.validate(true)
      return isValid.some((i) => i === false)
    }

    const onFinished = async (res, app) => {
      try {
        props.onFinished(res, app)
      } catch (ex) {
        console.error(ex)
      }
    }

    const onCancel = async () => {
      try {
        props.onCancel()
      } catch (ex) {
        console.error(ex)
      }
    }

    const { startDateValidation, endDateValidation } = getStartEndValidation({
      startDate: () => app.value.startDate,
      endDate: () => app.value.endDate,
    })

    const pickerConfig = {
      altInput: true,
      altFormat: localisation.getLocalisationPreferences().flatPickrFormat,
    }

    onMounted(async () => {
      try {
        app.value.appSpecificOptions = {}
        app.value.layoutBuilder = false
        app.value.owner = userId
        app.value.agency = agencyId
        app.value.account = accountId
        app.value.accountName = accountName
        app.value.type = 'embed'
        app.value.appVersion = formVersion
        app.value.syncForms = true

        await fetchLayouts()
        await fetchAccount()
        await fetchCampaigns()
        await fetchSchema()
        await setAccountTags()
        await getAppSpecificOptions(app.value)
        setActiveCampaign()

        wizardLoading.value = false
        popper.value.open()
      } catch (ex) {
        console.error(ex)
        await onCancel()
      }
    })

    return {
      wizardLoading,
      disableCampaignSelect,
      sidebarForm,
      popper,
      app,
      showAdvancedOptions,
      toggleAdvanced,
      layoutsLoading,
      layouts,
      campaigns,
      account,
      minStartDate,
      maxStartDate,
      minEndDate,
      maxEndDate,
      createNewApp,
      customValidationRules,
      startDateValidation,
      endDateValidation,
      pickerConfig,
    }
  },
})
</script>
